import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { useEffect, useState } from "react";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { Slider } from "@lending/components/Slider/Slider";
import { useGetPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useGetPersonalLoanApplicationQuery";
import { usePatchPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/usePatchPersonalLoanApplicationQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { getDefaultRequestedAmount } from "./utils";

function RequestedAmount() {
  const { goBack, goNext } = useFlowNavigation();
  const { l10n } = useLocalization();
  const { institution } = useGetInstitutionQuery();
  const { showToast } = useToastsContext();

  const { data: application, isLoading: isApplicationLoading } =
    useGetPersonalLoanApplicationQuery();

  const maxAmount = Number(institution.lending_requested_amount_params.max);
  const minAmount = Number(institution.lending_requested_amount_params.min);
  const amountStep = Number(institution.lending_requested_amount_params.step);
  const defaultAmountRounded = getDefaultRequestedAmount(minAmount, maxAmount, amountStep);

  const [sliderValue, setSliderValue] = useState(
    application?.requested_amount_cents ?? defaultAmountRounded
  );
  const [isSliderValueValid, setIsSliderValueValid] = useState(true);

  useEffect(() => {
    if (application?.requested_amount_cents) {
      setSliderValue(application.requested_amount_cents);
    }
  }, [application?.requested_amount_cents]);

  const { mutateAsync, isPending } = usePatchPersonalLoanApplicationQuery();

  const onSubmit = async () => {
    if (
      !isSliderValueValid ||
      !Number.isFinite(sliderValue) ||
      sliderValue > maxAmount ||
      sliderValue < minAmount
    ) {
      showToast({
        type: "negative",
        text: l10n.getString(
          "requested-amount-invalid-amount",
          null,
          "Please choose a valid amount."
        ),
      });
      return;
    }
    if (isApplicationLoading) {
      return;
    }

    if (!application?.id) {
      showToast({
        type: "negative",
        text: l10n.getString(
          "requested-amount-application-not-found",
          null,
          "Application not found. Please try again."
        ),
      });
      return;
    }
    try {
      await mutateAsync({
        params: {
          path: {
            uuid: application.id,
          },
        },
        body: { requested_amount_cents: sliderValue },
      });
      goNext();
    } catch {
      showToast({
        type: "negative",
        text: l10n.getString(
          "requested-amount-save-error",
          null,
          "There was an error saving your requested amount. Please try again."
        ),
      });
    }
  };

  return (
    <>
      <PageLayout>
        <PageContent>
          <PageHeader
            heading={l10n.getString(
              "requested-amount-heading",
              null,
              "How much would you like to borrow?"
            )}
            subheading={l10n.getString(
              "requested-amount-subheading",
              null,
              "Start with your preferred loan amount. You'll be able to update later."
            )}
          />
          <div className="margin--bottom--xxl">
            <Slider
              min={minAmount}
              max={maxAmount}
              step={amountStep}
              value={sliderValue}
              onChange={setSliderValue}
              onValidityChange={setIsSliderValueValid}
            />
          </div>
          <Row alignItems="center" justifyContent="space-between">
            <BackButton onClick={goBack} />
            <Button
              label={l10n.getString("button-label-next", null, "Next")}
              onClick={onSubmit}
              isLoading={isPending}
              disabled={isApplicationLoading}
            />
          </Row>
        </PageContent>
      </PageLayout>
    </>
  );
}

export { RequestedAmount };
